import React, { useState, useRef } from 'react';
import Upload from './components/Upload';
import Player from './components/Player';
import Controls from './components/Controls';
import ThumbnailStrip from './components/ThumbnailStrip';
import DownloadButton from './components/DownloadButton';
import './App.css';

/**
 * Main App component that orchestrates the video clipping workflow
 * 
 * State management:
 * - videoFile: The uploaded video file
 * - videoUrl: URL for video preview
 * - duration: Total video duration in seconds
 * - startTime: Selected start time for clipping
 * - endTime: Selected end time for clipping
 * - clippedVideo: The processed clipped video blob
 * - thumbnails: Array of thumbnail data for navigation
 * - isProcessing: Loading state for video processing
 * - error: Error messages to display
 */
function App() {
  // Core video state
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  
  // Clipping state
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [clippedVideo, setClippedVideo] = useState(null);
  
  // UI state
  const [thumbnails, setThumbnails] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Refs for video element and processing
  const videoRef = useRef(null);
  const processingRef = useRef(null);

  /**
   * Handle video file upload
   * Validates file type and creates preview URL
   */
  const handleFileUpload = (file) => {
    // Validate file type - expanded support
    const validTypes = [
      'video/mp4', 
      'video/quicktime', 
      'video/webm',
      'video/avi',
      'video/x-msvideo',
      'video/mpeg',
      'video/x-mpeg',
      'video/mp2t',
      'video/x-matroska'
    ];
    
    // Also check file extension for better compatibility
    const validExtensions = ['.mp4', '.mov', '.webm', '.avi', '.mpeg', '.mpg', '.mkv', '.ts'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setError('Please upload a supported video file (MP4, MOV, WebM, AVI, MPEG, MKV)');
      return;
    }

    // Validate file size (limit to 500MB for browser performance)
    const maxSize = 500 * 1024 * 1024; // 500MB
    if (file.size > maxSize) {
      setError('File size too large. Please upload a video smaller than 500MB.');
      return;
    }

    setError(null);
    setSuccess(null);
    setVideoFile(file);
    
    // Create URL for video preview
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    
    // Reset clipping state
    setClippedVideo(null);
    setThumbnails([]);
    setStartTime(0);
    setEndTime(0);
    
    // Show format information
    const formatInfo = getFormatInfo(file);
    if (formatInfo.needsConversion) {
      setSuccess(`Video uploaded! Note: ${formatInfo.message}`);
    } else {
      setSuccess('Video uploaded successfully!');
    }
  };

  /**
   * Handle video metadata loaded
   * Sets duration and initial end time
   */
  const handleVideoLoaded = (videoDuration) => {
    setDuration(videoDuration);
    setEndTime(videoDuration);
  };

  /**
   * Handle video clipping
   * Uses a simpler approach that preserves audio
   */
  const handleClipVideo = async () => {
    if (!videoFile) {
      setError('No video file available');
      return;
    }

    if (startTime >= endTime) {
      setError('End time must be greater than start time');
      return;
    }

    if (endTime - startTime < 1) {
      setError('Clip must be at least 1 second long');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('Starting video clipping with audio...');
      console.log('Start time:', startTime, 'End time:', endTime);
      
      // Create a video element for processing
      const video = document.createElement('video');
      video.src = URL.createObjectURL(videoFile);
      video.muted = false; // Keep audio enabled
      video.crossOrigin = 'anonymous';
      
      // Wait for video to load
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
        video.load();
      });
      
      // Create canvas for video processing
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        throw new Error('MediaRecorder not supported in this browser');
      }
      
      // Create a more comprehensive stream that includes audio
      const canvasStream = canvas.captureStream(30); // 30 FPS
      
      // Try to get audio from the video element
      let audioStream = null;
      try {
        // This approach works in some browsers
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaElementSource(video);
        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);
        audioStream = destination.stream;
      } catch (audioError) {
        console.warn('Could not create audio stream:', audioError);
      }
      
      // Create combined stream with video and audio
      const tracks = [...canvasStream.getVideoTracks()];
      if (audioStream) {
        tracks.push(...audioStream.getAudioTracks());
      }
      
      const combinedStream = new MediaStream(tracks);
      
      // Try different mime types for better compatibility
      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8,opus';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
      }
      
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: mimeType
      });
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        setClippedVideo(blob);
        setSuccess(`Video clipped successfully with audio! (${(endTime - startTime).toFixed(1)}s clip)`);
        console.log('Video clipping completed successfully');
        setIsProcessing(false);
        
        // Clean up
        URL.revokeObjectURL(video.src);
      };
      
      // Start recording
      mediaRecorder.start();
      
      // Seek to start time
      video.currentTime = startTime;
      await new Promise(resolve => {
        video.onseeked = resolve;
      });
      
      // Process frames with audio
      const processFrame = () => {
        return new Promise((resolve) => {
          const drawFrame = () => {
            if (video.currentTime >= endTime) {
              mediaRecorder.stop();
              resolve();
              return;
            }
            
            // Draw current frame to canvas
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Advance to next frame
            video.currentTime += 1/30; // 30 FPS
            
            if (video.currentTime < endTime) {
              requestAnimationFrame(drawFrame);
            } else {
              mediaRecorder.stop();
              resolve();
            }
          };
          
          drawFrame();
        });
      };
      
      // Start processing
      await processFrame();
      
    } catch (err) {
      console.error('Error clipping video:', err);
      setError(`Failed to clip video: ${err.message || 'Unknown error'}`);
      setIsProcessing(false);
    }
  };

  /**
   * Handle thumbnail generation
   * Creates thumbnails for timeline navigation
   */
  const handleGenerateThumbnails = async (thumbnailData) => {
    setThumbnails(thumbnailData);
  };

  /**
   * Handle thumbnail click - seek video to specific time
   */
  const handleThumbnailClick = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  /**
   * Handle download of clipped video
   */
  const handleDownload = () => {
    if (!clippedVideo) {
      setError('No clipped video available for download');
      return;
    }

    const url = URL.createObjectURL(clippedVideo);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clipped_video_${Date.now()}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /**
   * Get format information and conversion recommendations
   */
  const getFormatInfo = (file) => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    
    // Check for MPEG formats
    if (fileName.includes('.mpeg') || fileName.includes('.mpg') || 
        fileType.includes('mpeg') || fileType.includes('mp2t')) {
      return {
        needsConversion: true,
        message: 'MPEG format detected. For best results, convert to MP4 using FFmpeg or online converter.'
      };
    }
    
    // Check for AVI
    if (fileName.includes('.avi') || fileType.includes('avi')) {
      return {
        needsConversion: true,
        message: 'AVI format detected. May work but MP4 is recommended for best compatibility.'
      };
    }
    
    // Check for MKV
    if (fileName.includes('.mkv') || fileType.includes('matroska')) {
      return {
        needsConversion: true,
        message: 'MKV format detected. Convert to MP4 for guaranteed compatibility.'
      };
    }
    
    // Check for well-supported formats
    if (fileName.includes('.mp4') || fileName.includes('.webm') || fileName.includes('.mov')) {
      return {
        needsConversion: false,
        message: 'Well-supported format detected.'
      };
    }
    
    return {
      needsConversion: false,
      message: 'Format will be tested for compatibility.'
    };
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>Video Clipper</h1>
        <p>Upload a video, select a portion to clip, and download the result</p>
      </header>

      {/* File Upload Component */}
      <Upload 
        onFileUpload={handleFileUpload}
        isProcessing={isProcessing}
      />

      {/* Error and Success Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Video Player Component */}
      {videoUrl && (
        <Player
          videoUrl={videoUrl}
          videoRef={videoRef}
          onVideoLoaded={handleVideoLoaded}
          onGenerateThumbnails={handleGenerateThumbnails}
          processingRef={processingRef}
        />
      )}

      {/* Controls Component */}
      {videoFile && (
        <Controls
          duration={duration}
          startTime={startTime}
          endTime={endTime}
          onStartTimeChange={setStartTime}
          onEndTimeChange={setEndTime}
          onClipVideo={handleClipVideo}
          isProcessing={isProcessing}
        />
      )}

      {/* Thumbnail Navigation Strip */}
      {thumbnails.length > 0 && (
        <ThumbnailStrip
          thumbnails={thumbnails}
          onThumbnailClick={handleThumbnailClick}
        />
      )}

      {/* Download Component */}
      {clippedVideo && (
        <DownloadButton
          onDownload={handleDownload}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
}

export default App;

